import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, ChevronLeft, ChevronRight, Play, Pause,
  Volume2, VolumeX, ArrowLeft, Sparkles, ChevronDown,
  Share2, Copy, Check, Mail, MessageCircle, X,
  Zap, TrendingUp, ClipboardCheck, Eye, BarChart3, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { STAGES, StageVisual } from "@/pages/HowItWorksPage";

// Music: professional cinematic-electronic track served from /public.
// CC BY 3.0 — Jason Shaw / Audionautix, "Azimuth".
const MUSIC_URL = "/audio/cinematic.mp3";
// V8 engine — Plymouth Barracuda V8 ignition + acelerada (provided by user
// via Pixabay, "karlosdearma-motor-v8-plymouth-barracuda-encendido-acelerada-marcha").
// Trimmed to first 7.5s, loudness-normalized to -12 LUFS.
const V8_URL = "/audio/v8-engine.mp3";
const V8_BURST_MS = 7500;
// Time the intro overlay stays on screen after the press.
const INTRO_HOLD_MS = 7500;

const SCENE_MS = 16000;

// Each scene gets its own colour temperature so the deck "breathes"
const SCENE_GLOW = [
  { from: "rgba(255,138,0,0.30)",  to: "rgba(80,40,200,0.22)" }, // 01 booking — orange→indigo
  { from: "rgba(0,180,255,0.28)",  to: "rgba(0,90,180,0.18)" },  // 02 check-in — cyan
  { from: "rgba(255,90,0,0.30)",   to: "rgba(120,0,40,0.22)" },  // 03 workshop — fire orange
  { from: "rgba(120,255,200,0.30)", to: "rgba(0,80,80,0.20)" },  // 04 video — teal
  { from: "rgba(255,200,0,0.30)",  to: "rgba(120,30,0,0.22)" },  // 05 vhc — amber
  { from: "rgba(180,80,255,0.32)", to: "rgba(40,0,80,0.26)" },   // 06 magic — purple
  { from: "rgba(0,200,120,0.30)",  to: "rgba(0,80,40,0.22)" },   // 07 notify — green
  { from: "rgba(255,80,80,0.28)",  to: "rgba(120,0,40,0.22)" },  // 08 approve — red
  { from: "rgba(255,200,80,0.32)", to: "rgba(40,30,0,0.30)" },   // 09 handover — gold
];

// Each scene has a "kind" that the renderer switches on.
// We compose them in dramatic order: Problem → 9 stage walkthrough → Role tabs → Dealer gains → Finale.
const buildScenes = () => {
  const scenes = [];
  scenes.push({ kind: "problem" });
  STAGES.forEach((s) => scenes.push({ kind: "stage", stage: s }));
  scenes.push({ kind: "tabs" });
  scenes.push({ kind: "gains" });
  scenes.push({ kind: "adaptive" });
  scenes.push({ kind: "finale" });
  return scenes;
};

const PROBLEM_GLOW  = { from: "rgba(255,80,80,0.28)", to: "rgba(120,0,40,0.30)" };
const TABS_GLOW     = { from: "rgba(120,180,255,0.30)", to: "rgba(40,80,160,0.26)" };
const GAINS_GLOW    = { from: "rgba(180,255,140,0.30)", to: "rgba(0,80,40,0.22)" };
const ADAPTIVE_GLOW = { from: "rgba(255,180,80,0.28)",  to: "rgba(180,80,0,0.22)" };

export default function CinematicDemoPage() {
  const nav = useNavigate();
  // ?social=1 — used by the headless recorder to bake the downloadable
  // Facebook-shareable MP4. Shows the engine-start intro for ~3s, then
  // auto-fires the ignition (V8 burst → cinematic music swell), and runs the
  // deck with 10s per scene so the copy stays readable.
  const socialMode = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("social") === "1";
  const sceneMs = socialMode ? 10000 : SCENE_MS;
  const [phase, setPhase] = useState("intro"); // "intro" | "running"
  const [slide, setSlide] = useState(0);
  const [playing, setPlaying] = useState(true);
  // In LIVE boardroom mode, sound is OFF by default (presenter presses M to unmute).
  // In SOCIAL recording mode (?social=1), sound is ON by default so the recorded
  // video carries the music swell after the V8 ignition.
  const [muted, setMuted] = useState(!socialMode);
  const audioRef = useRef(null);
  const v8Ref = useRef(null);

  const SCENES = useMemo(() => buildScenes(), []);
  const total = SCENES.length;
  const scene = SCENES[Math.min(slide, total - 1)];
  const stageIndex = SCENES.slice(0, slide + 1).filter((s) => s.kind === "stage").length - 1;
  const glow =
    scene.kind === "problem"  ? PROBLEM_GLOW :
    scene.kind === "tabs"     ? TABS_GLOW :
    scene.kind === "gains"    ? GAINS_GLOW :
    scene.kind === "adaptive" ? ADAPTIVE_GLOW :
    scene.kind === "finale"   ? GAINS_GLOW :
    SCENE_GLOW[stageIndex >= 0 ? stageIndex % SCENE_GLOW.length : 0];

  const next = () => setSlide((s) => (s + 1) % total);
  const prev = () => setSlide((s) => (s - 1 + total) % total);

  // Auto-progress
  useEffect(() => {
    if (!playing || phase !== "running") return;
    if (scene.kind === "finale") return; // park on finale
    const t = setTimeout(() => setSlide((s) => Math.min(s + 1, total - 1)), sceneMs);
    return () => clearTimeout(t);
  }, [playing, slide, total, phase, scene.kind, sceneMs]);

  // Music settings
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = 0.45;
  }, []);
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.muted = muted;
    if (phase === "running" && !muted) {
      a.play().catch(() => { /* ignored */ });
    } else if (phase === "intro" || muted) {
      a.pause();
    }
  }, [muted, phase]);

  // Keyboard nav (only after intro)
  useEffect(() => {
    if (phase !== "running") return;
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft") prev();
      else if (e.key.toLowerCase() === "m") setMuted((m) => !m);
      else if (e.key.toLowerCase() === "p") setPlaying((p) => !p);
      else if (e.key === "Escape") nav("/");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const ignite = async () => {
    if (phase !== "intro") return;
    // Fire V8 engine sound (user gesture allows audio) — quick burst only
    const v8 = v8Ref.current;
    if (v8) {
      v8.volume = 0.9;
      v8.currentTime = 0;
      try { await v8.play(); } catch { /* ignored */ }
      // pause after the burst so it's a sharp ignition, not idling
      setTimeout(() => { try { v8.pause(); } catch { /* ignored */ } }, V8_BURST_MS);
    }
    // After the ignition, fade into scene 1. Audio stays muted by default
    // (boardroom-safe). Presenter can press M to unmute mid-show.
    setTimeout(() => {
      setPhase("running");
      const a = audioRef.current;
      if (a && !muted) {
        a.play().catch(() => { /* ignored */ });
      }
    }, INTRO_HOLD_MS);
  };

  // Auto-ignite for the social-recording mode — gives the recorder ~2.6s to
  // capture the engine-start splash, then fires the V8 burst, then the deck.
  useEffect(() => {
    if (!socialMode) return;
    if (phase !== "intro") return;
    const t = setTimeout(() => { ignite(); }, 2600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socialMode, phase]);

  const isLast    = scene.kind === "finale";
  const isProblem = scene.kind === "problem";
  const isTabs    = scene.kind === "tabs";
  const isGains   = scene.kind === "gains";

  return (
    <div className="cinematic-stage relative min-h-screen w-full overflow-hidden bg-black text-white" data-testid="cinematic-watch-page">
      {/* Audio elements */}
      <audio ref={audioRef} src={MUSIC_URL} loop preload="auto" />
      <audio ref={v8Ref} src={V8_URL} preload="auto" />

      {/* Intro phase — full-screen black, centred Engine Start button */}
      {phase === "intro" && (
        <IntroOverlay
          onIgnite={ignite}
          onSkip={() => { setPhase("running"); setMuted(false); }}
          muted={muted}
        />
      )}

      {/* Background — animated radial glow that shifts per scene */}
      <div
        key={`bg-${slide}`}
        className={`pointer-events-none absolute inset-0 transition-opacity duration-1000 ${phase === "intro" ? "opacity-0" : "opacity-100"}`}
        style={{
          background: `
            radial-gradient(60% 50% at 18% 20%, ${glow.from} 0%, transparent 60%),
            radial-gradient(70% 60% at 85% 85%, ${glow.to} 0%, transparent 65%),
            #000`,
        }}
      />
      {/* subtle grain overlay */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='120' height='120' filter='url(%23n)'/></svg>\")",
        }}
      />
      {/* Vignette */}
      <div className="pointer-events-none absolute inset-0" style={{ boxShadow: "inset 0 0 240px 80px rgba(0,0,0,0.85)" }} />

      {/* Top bar (only after intro) */}
      {phase === "running" && (
      <header className="relative z-10 flex items-center justify-between px-6 md:px-10 pt-6 animate-cinematic-in">
        <Link to="/" className="inline-flex items-center gap-2.5 group" data-testid="cinematic-home-link">
          <span className="grid place-items-center w-9 h-9 rounded-md bg-primary/20 border border-primary/40 backdrop-blur-md group-hover:bg-primary/30 transition-colors">
            <Sparkles className="w-4 h-4 text-primary" />
          </span>
          <span className="font-display font-black tracking-tight text-lg">ServiceSell <span className="text-primary">PRO</span></span>
        </Link>

        <div className="flex items-center gap-2">
          <ShareDemoButton />
          <button
            onClick={() => setMuted((m) => !m)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 backdrop-blur-md text-sm transition-colors"
            data-testid="cinematic-sound-btn"
            title="Mute / unmute (M)"
          >
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4 text-primary" />}
            <span className="hidden sm:inline">{muted ? "Unmute" : "Sound on"}</span>
          </button>
          <button
            onClick={() => setPlaying((p) => !p)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 backdrop-blur-md text-sm transition-colors"
            data-testid="cinematic-play-btn"
            title="Play / pause (P)"
          >
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <Link
            to="/pilot"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground font-bold text-sm hover:brightness-110 transition-all"
            data-testid="cinematic-skip-cta"
          >
            Book a discovery call <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </header>
      )}

      {/* Scene */}
      {phase === "running" && (
      <main className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12 pt-10 md:pt-16 pb-24">
        {scene.kind === "finale"   ? <FinaleScene   key="finale" />
        : scene.kind === "adaptive" ? <AdaptiveScene key="adaptive" />
        : scene.kind === "gains"    ? <GainsScene    key="gains" />
        : scene.kind === "tabs"     ? <TabsScene     key="tabs" />
        : scene.kind === "problem"  ? <ProblemScene  key="problem" />
        : <SceneBody key={scene.stage.id} stage={scene.stage} />}
      </main>
      )}

      {/* Bottom bar */}
      {phase === "running" && (
      <footer className="absolute z-10 bottom-0 left-0 right-0 px-6 md:px-10 pb-5 flex items-center gap-4 animate-cinematic-in">
        <button
          onClick={prev}
          className="w-10 h-10 rounded-full grid place-items-center bg-white/5 hover:bg-white/15 border border-white/10 backdrop-blur-md transition-colors"
          data-testid="cinematic-prev-btn"
          aria-label="Previous"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {/* Counter + dots + bar */}
        <div className="flex-1 flex flex-col gap-2">
          <div className="flex items-center justify-between text-xs font-mono uppercase tracking-[0.2em] text-white/60">
            <span data-testid="cinematic-counter">
              {isLast ? (
                <><span className="text-primary">END</span> · The bottom line</>
              ) : isGains ? (
                <><span className="text-primary">11</span> · What dealerships gain</>
              ) : isTabs ? (
                <><span className="text-primary">10</span> · One platform · four roles</>
              ) : isProblem ? (
                <><span className="text-primary">00</span> · The problem we solve</>
              ) : (
                <><span className="text-primary">{scene.stage?.n}</span> · {scene.stage?.label}</>
              )}
            </span>
            <span className="hidden sm:inline">{String(slide + 1).padStart(2, "0")} / {String(total + 1).padStart(2, "0")}</span>
          </div>
          <div className="flex items-center gap-1">
            {Array.from({ length: total + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setSlide(i)}
                aria-label={`Go to scene ${i + 1}`}
                data-testid={`cinematic-dot-${i}`}
                className={`relative h-1 flex-1 rounded-full overflow-hidden transition-all ${i < slide ? "bg-primary/60" : "bg-white/15"}`}
              >
                {i === slide && (
                  <span
                    key={`bar-${slide}-${playing ? 'p' : 's'}`}
                    className="absolute inset-y-0 left-0 bg-primary"
                    style={{
                      animation: playing ? `slide-bar ${sceneMs}ms linear forwards` : "none",
                      width: playing ? undefined : "0%",
                    }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={next}
          className="w-10 h-10 rounded-full grid place-items-center bg-primary/80 hover:bg-primary border border-primary/40 transition-colors"
          data-testid="cinematic-next-btn"
          aria-label="Next"
        >
          <ChevronRight className="w-5 h-5 text-primary-foreground" />
        </button>
      </footer>
      )}

      {/* Bar animation keyframe (Tailwind plugin handles it via custom anim, fallback CSS) */}
      <style>{`
        @keyframes slide-bar { from { width: 0% } to { width: 100% } }
        .cinematic-stage * { -webkit-tap-highlight-color: transparent; }
      `}</style>
    </div>
  );
}

// =====================================================================
// ShareDemoButton — copy link / WhatsApp / Email / native share
// =====================================================================
function ShareDemoButton({ variant = "pill" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? `${window.location.origin}/watch` : "/watch";
  const subject = "Watch the ServiceSell PRO demo (60 seconds)";
  const body = `Have a look at how this works — short cinematic walkthrough of the workshop process from booking to handover:\n\n${url}\n\nIt'll give you the full picture in about a minute.`;

  const tryNative = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: subject, text: body, url });
        return true;
      } catch { /* user cancelled */ }
    }
    return false;
  };

  const onShareClick = async () => {
    if (await tryNative()) return;
    setOpen(true);
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Demo link copied — paste it anywhere");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — please select & copy manually");
    }
  };

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(`${subject}\n${url}`)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const smsHref = `sms:?&body=${encodeURIComponent(`${subject}\n${url}`)}`;

  const triggerCls = variant === "pill"
    ? "inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 backdrop-blur-md text-sm transition-colors"
    : "inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 hover:bg-white/5 text-white/90 font-bold text-base transition-colors";

  return (
    <>
      <button
        onClick={onShareClick}
        className={triggerCls}
        data-testid="cinematic-share-btn"
        title="Share this demo"
      >
        <Share2 className="w-4 h-4" />
        <span className={variant === "pill" ? "hidden sm:inline" : ""}>Share</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[80] grid place-items-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setOpen(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl bg-zinc-950 border border-white/10 shadow-2xl overflow-hidden animate-fade-up"
            data-testid="cinematic-share-modal"
          >
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-primary mb-1">Share</div>
                <h3 className="font-display font-black text-xl">Send the demo to anyone.</h3>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/50 hover:text-white" data-testid="cinematic-share-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="rounded-lg border border-white/10 bg-white/5 p-3 flex items-center gap-2">
                <span className="font-mono text-xs text-white/80 flex-1 truncate" data-testid="cinematic-share-url">{url}</span>
                <button
                  onClick={onCopy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-bold hover:brightness-110 transition-all"
                  data-testid="cinematic-share-copy"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <a
                  href={whatsappHref}
                  target="_blank" rel="noreferrer"
                  className="flex flex-col items-center gap-1.5 px-3 py-3 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-colors text-xs font-semibold"
                  data-testid="cinematic-share-whatsapp"
                >
                  <MessageCircle className="w-5 h-5 text-emerald-400" />
                  WhatsApp
                </a>
                <a
                  href={mailHref}
                  className="flex flex-col items-center gap-1.5 px-3 py-3 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-colors text-xs font-semibold"
                  data-testid="cinematic-share-email"
                >
                  <Mail className="w-5 h-5 text-blue-400" />
                  Email
                </a>
                <a
                  href={smsHref}
                  className="flex flex-col items-center gap-1.5 px-3 py-3 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-colors text-xs font-semibold"
                  data-testid="cinematic-share-sms"
                >
                  <MessageCircle className="w-5 h-5 text-primary" />
                  SMS
                </a>
              </div>
              <p className="text-xs text-white/50 text-center pt-1">
                Anyone with this link can watch — no sign-up needed.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// =====================================================================
// Intro overlay — Engine Start button → V8 ignition → cinematic deck
// =====================================================================
function IntroOverlay({ onIgnite, onSkip, muted }) {
  const [pressed, setPressed] = useState(false);
  const [fading, setFading] = useState(false);

  const handlePress = () => {
    if (pressed) return;
    setPressed(true);
    onIgnite();
    // start fade just before transition into deck
    setTimeout(() => setFading(true), INTRO_HOLD_MS - 600);
  };

  return (
    <div
      className={`fixed inset-0 z-30 flex flex-col items-center justify-center bg-black overflow-hidden transition-opacity duration-700 ${fading ? "opacity-0 pointer-events-none" : "opacity-100"}`}
      data-testid="cinematic-intro"
    >
      {/* Ambient glows */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(60% 50% at 50% 35%, rgba(255,90,0,0.20) 0%, transparent 65%),
            radial-gradient(50% 50% at 50% 90%, rgba(120,0,40,0.30) 0%, transparent 70%)`,
        }}
      />
      {/* heat shimmer ring on press */}
      {pressed && (
        <>
          <span className="absolute inset-0 m-auto w-[420px] h-[420px] rounded-full ring-[3px] ring-primary/30 animate-pulse-ring" />
          <span className="absolute inset-0 m-auto w-[420px] h-[420px] rounded-full ring-[3px] ring-primary/20 animate-pulse-ring" style={{ animationDelay: "300ms" }} />
        </>
      )}

      {/* Brand */}
      <div className="absolute top-6 left-0 right-0 flex justify-center">
        <div className="inline-flex items-center gap-2.5">
          <span className="grid place-items-center w-9 h-9 rounded-md bg-primary/20 border border-primary/40 backdrop-blur-md">
            <Sparkles className="w-4 h-4 text-primary" />
          </span>
          <span className="font-display font-black tracking-tight text-lg">ServiceSell <span className="text-primary">PRO</span></span>
        </div>
      </div>

      {/* Skip + Share */}
      <div className="absolute top-6 right-6 flex items-center gap-2">
        <ShareDemoButton />
        <button
          onClick={onSkip}
          className="text-xs uppercase tracking-[0.25em] text-white/50 hover:text-white/90 transition-colors"
          data-testid="cinematic-skip-intro"
        >
          Skip intro →
        </button>
      </div>

      <div className="relative z-10 flex flex-col items-center text-center px-6 animate-cinematic-in">
        <div className="text-[11px] font-mono uppercase tracking-[0.4em] text-primary mb-5">
          The customer journey · in 60 seconds
        </div>
        <h1
          className="font-display font-black tracking-[-0.03em] leading-[0.95]"
          style={{ fontSize: "clamp(40px, 5.5vw, 84px)" }}
        >
          Press to start.
        </h1>
        <p className="mt-4 max-w-md text-white/65 text-base md:text-lg">
          Hit the ignition. We&apos;ll walk you through every step of the workshop process — from booking to handover.
        </p>

        {/* The push button */}
        <button
          onClick={handlePress}
          aria-label="Engine start — begin the demo"
          disabled={pressed}
          className={`group relative w-44 h-44 md:w-52 md:h-52 rounded-full mt-10 transition-all duration-300 ${
            pressed ? "scale-95" : "hover:scale-105"
          }`}
          data-testid="cinematic-engine-start-btn"
        >
          {/* outer ring */}
          <span className="absolute inset-0 rounded-full bg-gradient-to-br from-zinc-700 via-zinc-900 to-black ring-1 ring-zinc-600 shadow-[0_0_60px_-10px_rgba(0,0,0,0.9)]" />
          {/* knurled metal ring */}
          <span className="absolute inset-2 rounded-full bg-gradient-to-br from-zinc-500 via-zinc-700 to-zinc-900 ring-1 ring-zinc-400/40" />
          {/* inner green button — pinned to SSP brand accent */}
          <span className={`absolute inset-5 rounded-full bg-gradient-to-br from-primary/85 via-primary to-primary/60 shadow-inner transition-all duration-300 ${
            pressed ? "shadow-[0_0_60px_12px_rgba(210,224,49,0.65)]" : "group-hover:shadow-[0_0_40px_6px_rgba(210,224,49,0.5)]"
          }`} />
          {/* highlight */}
          <span className="absolute inset-5 rounded-full bg-gradient-to-b from-white/25 via-transparent to-transparent" />
          {/* halo */}
          <span className={`absolute -inset-1 rounded-full ring-2 ring-primary/40 ${pressed ? "animate-ping" : "animate-pulse"}`} />
          {/* label */}
          <span className="absolute inset-0 flex flex-col items-center justify-center text-primary-foreground font-display tracking-widest">
            <span className="text-xs font-bold uppercase opacity-80">Engine</span>
            <span className="text-2xl md:text-3xl font-extrabold">START</span>
            <span className="text-[10px] font-bold uppercase opacity-70 mt-0.5">Push</span>
          </span>
        </button>

        <div className={`mt-6 text-xs font-bold uppercase tracking-[0.3em] transition-colors ${pressed ? "text-primary" : "text-muted-foreground"}`}>
          {pressed ? "Igniting…" : "Press the button"}
        </div>

        {/* hint — matches the audio default (silent in live mode, sound-on in social/recording mode) */}
        {!pressed && (
          <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-white/30">
            {muted ? "Silent mode \u2014 narration off" : "Sound on \u2014 music swell after ignition"}
          </div>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// Scene body — split layout, big number watermark, animated content
// =====================================================================
function SceneBody({ stage }) {
  return (
    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center min-h-[68vh]">
      {/* Massive scene number watermark behind copy */}
      <div className="lg:col-span-6 relative">
        <div
          aria-hidden
          className="absolute -top-10 -left-6 md:-left-10 font-display font-black leading-none select-none animate-cinematic-num"
          style={{ fontSize: "clamp(180px, 26vw, 380px)", color: "white", opacity: 0.06, letterSpacing: "-0.05em" }}
        >
          {stage.n}
        </div>

        <div className="relative animate-cinematic-in">
          <div className="text-[11px] font-mono uppercase tracking-[0.35em] text-primary mb-6 inline-flex items-center gap-2">
            <span className="w-8 h-px bg-primary" /> {stage.label}
          </div>
          <h2
            className="font-display font-black tracking-[-0.03em] leading-[0.92] text-white"
            style={{ fontSize: "clamp(40px, 5.6vw, 88px)" }}
            data-testid="cinematic-title"
          >
            {stage.title}
          </h2>
          <p className="mt-6 text-primary text-xl md:text-2xl font-semibold max-w-xl leading-snug">
            {stage.sub}
          </p>
          <p className="mt-5 text-white/70 text-base md:text-lg leading-relaxed max-w-xl">
            {stage.body}
          </p>
          <ul className="mt-7 space-y-2.5 max-w-xl" data-testid="cinematic-bullets">
            {stage.bullets.map((b, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-white/85 text-sm md:text-base"
                style={{ animation: "fade-up 0.6s both", animationDelay: `${600 + i * 150}ms` }}
              >
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Visual stage — softly glowing card */}
      <div className="lg:col-span-6 relative">
        <div className="relative">
          {/* halo */}
          <div
            aria-hidden
            className="absolute -inset-10 rounded-full opacity-70 blur-3xl pointer-events-none animate-cinematic-glow"
            style={{ background: `radial-gradient(circle, hsl(var(--primary) / 0.45) 0%, transparent 65%)` }}
          />
          <div
            className="relative rounded-3xl p-6 md:p-10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] border border-white/10 backdrop-blur-xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] animate-cinematic-in"
            style={{ animationDelay: "200ms" }}
          >
            <div className="origin-center animate-ken-burns">
              <StageVisual kind={stage.visual} />
            </div>
          </div>
          {/* corner caption */}
          <div className="absolute -bottom-3 left-6 px-3 py-1.5 rounded-full bg-black border border-white/10 text-[10px] uppercase tracking-[0.3em] font-mono text-white/60">
            ServiceSell PRO&trade; · live UI
          </div>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// GainsScene — "What dealerships gain" five-card scoreboard
// =====================================================================
function GainsScene() {
  const items = [
    { icon: Clock,          t: "8.5 hours/week back",        d: "Snap the diary at 4pm — AI fires every pre-call SMS + email overnight." },
    { icon: Zap,            t: "Faster approvals",          d: "Customers tap to authorise straight from a video link." },
    { icon: TrendingUp,     t: "Increased upsell conversion", d: "Plain-English AI + traffic-light grading lift conversion by double digits." },
    { icon: ClipboardCheck, t: "Reduced advisor admin",     d: "Pre-built templates and AI write-ups give advisors hours back." },
    { icon: Eye,            t: "Customer transparency",     d: "Customers see exactly what the technician sees, in real time." },
    { icon: BarChart3,      t: "Live reporting",            d: "Real-time funnel: quotes won, lost, reconsidered — by advisor and branch." },
  ];
  return (
    <div className="min-h-[68vh] flex flex-col justify-center animate-cinematic-in" data-testid="cinematic-gains-scene">
      <div className="text-center mb-10">
        <div className="text-[11px] font-mono uppercase tracking-[0.35em] text-primary mb-5 inline-flex items-center gap-2">
          <span className="w-8 h-px bg-primary" /> For dealer principals <span className="w-8 h-px bg-primary" />
        </div>
        <h2
          className="font-display font-black tracking-[-0.03em] leading-[0.95]"
          style={{ fontSize: "clamp(40px, 6vw, 96px)" }}
        >
          What dealerships <span className="text-primary">gain</span>.
        </h2>
        <p className="mt-5 text-white/70 text-base md:text-lg max-w-2xl mx-auto">
          Six outcomes you can measure from week one — without changing your DMS or retraining your team.
        </p>
        <p className="mt-3 text-[11px] font-mono uppercase tracking-[0.3em] text-white/35" data-testid="cinematic-gains-credibility">
          Powered by enterprise-grade AI models · UK GDPR-compliant · Hosted in the UK
        </p>
      </div>

      <ul className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 max-w-[1400px] mx-auto w-full" data-testid="cinematic-gains-list">
        {items.map((it, i) => (
          <li
            key={it.t}
            className="group relative rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-white/[0.01] hover:border-primary/40 backdrop-blur-xl p-6 shadow-[0_30px_60px_-25px_rgba(0,0,0,0.8)] transition-all"
            style={{ animation: "fade-up 0.7s both", animationDelay: `${250 + i * 140}ms` }}
            data-testid={`cinematic-gains-item-${i}`}
          >
            {/* Halo */}
            <div
              aria-hidden
              className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
              style={{ background: "radial-gradient(60% 60% at 50% 0%, hsl(var(--primary) / 0.18), transparent 70%)" }}
            />
            <span className="grid place-items-center w-12 h-12 rounded-xl bg-primary/15 border border-primary/30">
              <it.icon className="w-6 h-6 text-primary" strokeWidth={2} />
            </span>
            <div className="mt-5 font-display font-bold text-xl leading-tight">{it.t}</div>
            <p className="mt-2 text-sm text-white/65 leading-relaxed">{it.d}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

// =====================================================================
// AdaptiveScene — "Built for every dealership."
// =====================================================================
function AdaptiveScene() {
  const marques = ["Toyota", "Hyundai", "BMW", "Audi", "Ford", "VW", "Vauxhall", "Mercedes", "Kia", "Honda", "Nissan", "Independent"];
  return (
    <div className="min-h-[68vh] grid place-items-center text-center animate-cinematic-in" data-testid="cinematic-adaptive-scene">
      <div className="max-w-4xl">
        <div className="text-[11px] font-mono uppercase tracking-[0.35em] text-primary mb-6 inline-flex items-center gap-2">
          <span className="w-8 h-px bg-primary" /> Built once. Evolves with you. <span className="w-8 h-px bg-primary" />
        </div>
        <h2
          className="font-display font-black tracking-[-0.03em] leading-[0.95]"
          style={{ fontSize: "clamp(42px, 6.4vw, 104px)" }}
        >
          Built for every dealership.<br />
          <span className="text-primary">Main-dealer, franchise or independent.</span>
        </h2>
        <p className="mt-6 text-white/75 text-base md:text-xl max-w-2xl mx-auto leading-relaxed">
          ServiceSell PRO transforms any aftersales workshop — main-dealer or independent. As part of the set-up, we personalise the platform to your manufacturer, your VHC sheet, your price matrix and your brand.
        </p>
        <ul className="mt-10 flex flex-wrap items-center justify-center gap-2 max-w-3xl mx-auto" data-testid="cinematic-adaptive-marques">
          {marques.map((m, i) => (
            <li
              key={m}
              className="px-4 py-2 rounded-full border border-white/15 bg-white/[0.04] backdrop-blur text-sm font-bold tracking-wide text-white/85"
              style={{ animation: "fade-up 0.6s both", animationDelay: `${250 + i * 70}ms` }}
            >
              {m}
            </li>
          ))}
        </ul>
        <p className="mt-10 text-[12px] font-mono uppercase tracking-[0.3em] text-white/45">
          One platform · every manufacturer · personalised to your workshop
        </p>
      </div>
    </div>
  );
}

// =====================================================================
// Finale — closing slide with big CTA
// =====================================================================
function FinaleScene() {
  return (
    <div className="min-h-[68vh] grid place-items-center text-center animate-cinematic-in">
      <div className="max-w-3xl">
        <div className="text-[11px] font-mono uppercase tracking-[0.35em] text-primary mb-6 inline-flex items-center gap-2">
          <span className="w-8 h-px bg-primary" /> The bottom line <span className="w-8 h-px bg-primary" />
        </div>
        <h2
          className="font-display font-black tracking-[-0.03em] leading-[0.92]"
          style={{ fontSize: "clamp(48px, 7vw, 120px)" }}
        >
          Sell more work.<br />
          <span className="text-primary">In half the time.</span>
        </h2>
        <p className="mt-6 text-white/85 text-lg md:text-2xl font-semibold tracking-[-0.01em]" data-testid="cinematic-finale-tagline">
          The commercial operating system for modern aftersales departments.
        </p>
        <p className="mt-6 text-white/60 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
          One platform. Booking → walkaround video → traffic-light health check → AI-friendly explanation → one-tap approval → handover.
          Built for technicians, loved by customers.
        </p>
        <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
          <Link
            to="/register"
            className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-primary text-primary-foreground font-bold text-lg shadow-[0_20px_60px_-15px_hsl(var(--primary))] hover:scale-[1.03] active:scale-[0.98] transition-all"
            data-testid="cinematic-finale-cta"
          >
            Start your free trial
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
          <ShareDemoButton variant="big" />
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 text-white/80 hover:bg-white/5 transition-colors"
            data-testid="cinematic-finale-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to homepage
          </Link>
        </div>
        <div className="mt-10 text-xs font-mono uppercase tracking-[0.3em] text-white/40">
          ServiceSell PRO&trade; · 2026
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// ProblemScene — opening hook: why workshops lose work today
// =====================================================================
function ProblemScene() {
  const stats = [
    { figure: "47%", label: "of recommended work goes unsold", tone: "destructive" },
    { figure: "8 min", label: "average advisor call to explain a job", tone: "warning" },
    { figure: "1 in 3", label: "customers say they didn't understand", tone: "warning" },
  ];
  return (
    <div className="min-h-[68vh] grid lg:grid-cols-12 gap-10 lg:gap-16 items-center animate-cinematic-in" data-testid="cinematic-problem-scene">
      <div className="lg:col-span-7 relative">
        <div
          aria-hidden
          className="absolute -top-12 -left-6 md:-left-10 font-display font-black leading-none select-none animate-cinematic-num"
          style={{ fontSize: "clamp(180px, 26vw, 380px)", color: "white", opacity: 0.05, letterSpacing: "-0.05em" }}
        >
          00
        </div>
        <div className="relative">
          <div className="text-[11px] font-mono uppercase tracking-[0.35em] text-destructive mb-6 inline-flex items-center gap-2">
            <span className="w-8 h-px bg-destructive" /> The problem
          </div>
          <h2
            className="font-display font-black tracking-[-0.03em] leading-[0.92] text-white"
            style={{ fontSize: "clamp(40px, 5.6vw, 88px)" }}
            data-testid="cinematic-problem-title"
          >
            Workshops lose work <br />
            <span className="text-destructive">they&apos;ve already won.</span>
          </h2>
          <p className="mt-6 text-white/75 text-lg md:text-xl leading-snug max-w-xl">
            The technician knows what&apos;s wrong. The customer doesn&apos;t. Between a phone call, a paper sheet and a hurried estimate, half the recommended work walks out of the door.
          </p>
          <ul className="mt-7 space-y-2.5 max-w-xl">
            {[
              "Phone calls get missed — voicemails and call-backs eat the day.",
              "Customers can't picture what's wrong — they decline what they don't understand.",
              "Advisors retell the same story five times. Sales suffer, mood drops.",
            ].map((b, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-white/85 text-sm md:text-base"
                style={{ animation: "fade-up 0.6s both", animationDelay: `${600 + i * 150}ms` }}
              >
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-destructive shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="lg:col-span-5 grid gap-4">
        {stats.map((s, i) => {
          const ring = s.tone === "destructive" ? "border-destructive/40" : "border-warning/40";
          const text = s.tone === "destructive" ? "text-destructive" : "text-warning";
          return (
            <div
              key={s.figure}
              className={`rounded-2xl border ${ring} bg-white/[0.03] backdrop-blur-xl p-6 md:p-7`}
              style={{ animation: "fade-up 0.7s both", animationDelay: `${300 + i * 160}ms` }}
              data-testid={`cinematic-problem-stat-${i}`}
            >
              <div className={`font-display font-black tracking-tight ${text}`} style={{ fontSize: "clamp(40px, 5vw, 72px)" }}>
                {s.figure}
              </div>
              <div className="text-white/70 text-sm md:text-base mt-1">{s.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =====================================================================
// TabsScene — the four-role operating system
// =====================================================================
function TabsScene() {
  const roles = [
    { icon: ClipboardCheck, t: "Service Advisor", d: "Check-ins, welcome SMS/email, customer messaging." },
    { icon: Zap,            t: "Workshop Control", d: "Loading lanes, QC sheets, fix-it-right tracking." },
    { icon: Eye,            t: "Technician",      d: "Guided video, traffic-light VHC, AI write-ups." },
    { icon: BarChart3,      t: "Manager",         d: "Speed-to-authorise, % sold, conversion analytics." },
  ];
  return (
    <div className="min-h-[68vh] flex flex-col justify-center animate-cinematic-in" data-testid="cinematic-tabs-scene">
      <div className="text-center mb-12">
        <div className="text-[11px] font-mono uppercase tracking-[0.35em] text-primary mb-5 inline-flex items-center gap-2">
          <span className="w-8 h-px bg-primary" /> One platform · four roles <span className="w-8 h-px bg-primary" />
        </div>
        <h2
          className="font-display font-black tracking-[-0.03em] leading-[0.95]"
          style={{ fontSize: "clamp(40px, 6vw, 96px)" }}
          data-testid="cinematic-tabs-title"
        >
          Built for the <span className="text-primary">whole team.</span>
        </h2>
        <p className="mt-5 text-white/70 text-base md:text-lg max-w-2xl mx-auto">
          Every role gets a tab built around their day. No more bouncing between systems — and no more leaving cash on the table.
        </p>
      </div>

      <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-[1400px] mx-auto w-full">
        {roles.map((r, i) => (
          <li
            key={r.t}
            className="group relative rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-white/[0.01] hover:border-primary/40 backdrop-blur-xl p-6 shadow-[0_30px_60px_-25px_rgba(0,0,0,0.8)] transition-all"
            style={{ animation: "fade-up 0.7s both", animationDelay: `${250 + i * 140}ms` }}
            data-testid={`cinematic-tabs-role-${i}`}
          >
            <div
              aria-hidden
              className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
              style={{ background: "radial-gradient(60% 60% at 50% 0%, hsl(var(--primary) / 0.18), transparent 70%)" }}
            />
            <span className="grid place-items-center w-12 h-12 rounded-xl bg-primary/15 border border-primary/30">
              <r.icon className="w-6 h-6 text-primary" strokeWidth={2} />
            </span>
            <div className="mt-5 font-display font-bold text-xl leading-tight">{r.t}</div>
            <p className="mt-2 text-sm text-white/65 leading-relaxed">{r.d}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

